import type { DerivedWalletId } from '@halo/wallet-app';
import {
  EVMGasPaymentMethod,
  type BaseTransactionFeeResult,
  type TronTransactionFeeResult,
} from '@halo/wallet-app';
import {
  type Net,
  type CoinState,
  type TransactionFeeResult,
  ChainType,
  BIF,
  EVMNetGasPaymentMethod,
} from '@halo/wallet-app';
import { useWalletStore } from '@/wallet';
import type { EvmGasSettingInfo } from './gas-settings-panel';

export interface UseGasFeeProps {
  accountId: DerivedWalletId | undefined;
  net: Net | undefined;
  coin: CoinState | undefined;
  to: string;
  amount: string;
  trigger: object | undefined;
}

export function useGasFee({ accountId, net, coin, to, amount, trigger }: UseGasFeeProps) {
  const [loading, setLoading] = useState(false);
  const [gasInfo, setGasInfo] = useState<TransactionFeeResult>();
  const { curFiat, getTransactionFee, getAccountNetMainCoin } = useWalletStore();
  const [evmPaymentMethod, setEvmPaymentMethod] = useState<EVMGasPaymentMethod>(
    [EVMNetGasPaymentMethod.Ethereum].includes(net?.chainName as any)
      ? EVMGasPaymentMethod.EIP1559
      : EVMGasPaymentMethod.BID
  );
  const [gasLevelInfo, setGasLevelInfo] = useState<EvmGasSettingInfo>();
  const [mainCoin, setMainCoin] = useState<CoinState>();
  const [error, setError] = useState<Error>();

  const showEvmPaymentMethod = useMemo(() => {
    return [EVMNetGasPaymentMethod.Ethereum].includes(net?.chainName as any);
  }, [net?.chainName]);

  const totalGasFee = useMemo(() => {
    let gasFee = '0';
    let currencyGasFee = '0';
    switch (net?.chainType) {
      case ChainType.EVM:
        if (gasLevelInfo) {
          gasFee = gasLevelInfo.info.gasFee;
          currencyGasFee = gasLevelInfo.info.currencyGasFee || '0';
        }
        break;
      default:
        if (gasInfo) {
          const gasInfo_ = gasInfo as BaseTransactionFeeResult;
          gasFee =
            gasInfo_.gasFee &&
            BIF.from(gasInfo_.gasFee)
              .sum(gasInfo_.activeFee || 0)
              .toString();

          currencyGasFee =
            (gasInfo_.currencyGasFee &&
              BIF.from(gasInfo_.currencyGasFee)
                .sum(gasInfo_.currencyActiveFee || '0')
                .toString()) ||
            '0';
        }
        break;
    }
    return { gasFee, currencyGasFee, mainCoin, gasInfo };
  }, [gasInfo, gasLevelInfo, net?.chainType, mainCoin]);

  const validateGasFee = useMemo(() => {
    const result = {
      validateMainCoin: false,
      validateCoin: false,
      validate: false,
      message: '',
    };

    if (accountId && net && coin && amount && mainCoin?.balance && !error) {
      const { gasFee, gasInfo } = totalGasFee;

      if (
        gasInfo &&
        ((gasInfo as TronTransactionFeeResult).isUsedBandwidth ||
          (gasInfo as TronTransactionFeeResult).isUsedEnergy)
      ) {
        result.validate = true;
      } else {
        try {
          const needAmount = coin.isMain ? BIF.from(amount).sum(gasFee).toString() : amount;

          const mainValidate = BIF.from(mainCoin?.balance || '0').gt(gasFee);
          const coinValidate = BIF.from(coin.balance).gt(needAmount);

          result.validateMainCoin = mainValidate;
          result.validateCoin = coinValidate;
          result.validate = mainValidate && coinValidate;
        } catch (err) {
          console.warn(err);
          result.validate = false;
          result.message = 'Invalid gas fee';
        }
      }
    }

    return result;
  }, [accountId, net, coin, totalGasFee, amount, mainCoin?.balance, error]);
  const getGasFeeInfo = useCallback(() => {
    if (!trigger || !Number(amount) || !to || !accountId || !net || !coin) return;

    if (net.chainType === ChainType.EVM && !evmPaymentMethod) {
      return;
    }

    setLoading(true);
    setError(undefined);

    getTransactionFee(accountId, net, coin, curFiat.symbol, {
      to,
      amount,
      evmPaymentMethod,
    })
      .then((res) => {
        setGasInfo(res);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
        setGasInfo(undefined);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    accountId,
    amount,
    coin,
    curFiat?.symbol,
    getTransactionFee,
    net,
    to,
    evmPaymentMethod,
    trigger,
  ]);

  useEffect(() => {
    getGasFeeInfo();
  }, [getGasFeeInfo]);

  useEffect(() => {
    if (!accountId || !net) return;
    getAccountNetMainCoin(accountId, net.id).then(setMainCoin);
  }, [accountId, getAccountNetMainCoin, net]);

  return useMemo(
    () => ({
      loading,
      error,
      net,
      showEvmPaymentMethod,
      evmPaymentMethod,
      setEvmPaymentMethod,
      gasInfo,
      gasLevelInfo,
      setGasLevelInfo,
      mainCoin,
      totalGasFee,
      validateGasFee,
      getGasFeeInfo,
    }),
    [
      loading,
      error,
      net,
      showEvmPaymentMethod,
      evmPaymentMethod,
      gasInfo,
      gasLevelInfo,
      totalGasFee,
      mainCoin,
      validateGasFee,
      getGasFeeInfo,
    ]
  );
}

export type UseGasFeeResult = ReturnType<typeof useGasFee>;

export type TotalGasFee = ReturnType<typeof useGasFee>['totalGasFee'];
